import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router-dom";
import { Link } from "@mui/material";
import {
  findGetParameter,
  newPath
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";

import moment from "moment";
import "moment-timezone";

import {
  getGameSystems,
  handlePagingAll
} from "../../components/api/APIUtilities";
import { mapData, stringToUrl } from "./utils/event";
import EventsPlayDefault from "./EventsPlay/EventsPlayDefault";
import {
  useProcessing,
  useSignout
} from "../../components/user/BCPAuthReducers";
import { signout } from "../../components/user/BCPAuth";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { filterAndSortEvents } from "../../components/utilities/SortFilterFunctions";
import { useTranslation } from "react-i18next";

const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const SUPPORTS_BCP_PLATFORM_FEATURES =
  getEnvironmentVariable("SUPPORTS_BCP_PLATFORM_FEATURES") === "true";

function a11yProps(index) {
  return {
    id: `event-tab-${index}`,
    "aria-controls": `event-tabpanel-${index}`
  };
}

const tabIndexes = {
  current: 0,
  past: 1,
  following: 2
};

export default function EventsPlay() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const { t } = useTranslation("translation", {
    keyPrefix: "pages.play.EventsPlay"
  });

  const [loading, setLoading] = useState(true);
  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystemDict, setGameSystemDict] = useState({});

  const [playersCurrent, setPlayersCurrent] = useState(null);
  const [playersPast, setPlayersPast] = useState(null);
  const [playersFollowing, setPlayersFollowing] = useState(null);

  const [filteredEvents, setFilteredEvents] = useState(null);

  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [toggleMap, setToggleMap] = useState(true);
  const [activeEvents, setActiveEvents] = useState(null);

  const activeTabParam = findGetParameter("active_tab");
  const [activeTab, setActiveTab] = useState(null);

  const useSignoutReducer = useSignout();
  const useProcessingReducer = useProcessing();

  const handleTabChange = async (e, newValue) => {
    setLoading(true);
    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newValue) {
        var params = {
          active_tab: tabIndex
        };
        path = newPath(params);

        return true;
      }
    });
    setActiveTab(newValue);
    navigate(path, { replace: false });
    setLoading(false);
  };

  const loadEvents = async (activeTab, gameSystemDict) => {
    try {
      let queryStringParameters = {};
      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
        queryStringParameters.gameType =
          getEnvironmentVariable("SINGLE_GAME_TYPE");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      // Current Tab
      if (activeTab === 0) {
        if (!playersCurrent) {
          let eventData = await handlePagingAll("/events", {
            ...queryStringParameters,
            playerEvents: true,
            toEvents: true,
            eventEndDateFrom: moment()
              .add(-3, "days")
              .format("YYYY-MM-DDTHH:mm")
          });

          eventData = mapData(eventData, gameSystemDict, moment);

          setPlayersCurrent(eventData);
          setLoading(false);
        }
      }

      // Past Tab
      if (activeTab === 1) {
        if (!playersPast) {
          let eventData = await handlePagingAll("/events", {
            ...queryStringParameters,
            playerEvents: true,
            toEvents: true,
            eventEndDateTo: moment().add(-3, "days").format("YYYY-MM-DDTHH:mm")
          });

          eventData = mapData(eventData, gameSystemDict, moment);

          setPlayersPast(eventData);
          setLoading(false);
        }
      }

      // Following Tab
      if (activeTab === 2) {
        if (!playersFollowing) {
          let eventData = await handlePagingAll("/events", {
            ...queryStringParameters,
            followingEvents: true
          });

          eventData = mapData(eventData, gameSystemDict, moment);

          setPlayersFollowing(eventData);
          setLoading(false);
        }
      }
    } catch (err) {
      postNotification("error", err);
    }
  };

  useEffect(() => {
    const check = async () => {
      if (!authContext?.authState?.identity?.userId) {
        navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
        return;
      }
      try {
        var initialTab =
          tabIndexes[
            activeTabParam ? activeTabParam.toLowerCase() : "current"
          ] || 0;
        setActiveTab(initialTab);
      } catch (err) {
        //
      }
    };

    check();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const go = async () => {
      try {
        let _gameSystems = gameSystems;
        if (!_gameSystems) {
          _gameSystems = await getGameSystems({
            inclSystem: true
          });
          setGameSystems(_gameSystems);
        }

        let _gameSystemDict = gameSystemDict;
        if (!_gameSystemDict && typeof gameSystemDict === "object") {
          _gameSystemDict = _gameSystems.forEach((gameSystem) => {
            gameSystemDict[gameSystem.id] = gameSystem;
          });
          setGameSystemDict(_gameSystemDict);
        }

        loadEvents(activeTab, _gameSystemDict);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          try {
            if (SUPPORTS_BCP_PLATFORM_FEATURES) {
              navigate("/logout");
            } else {
              await signout(useSignoutReducer, {
                setProcessing: useProcessingReducer,
                navigate: navigate
              });
            }
          } catch (err) {
            postNotification("error", err);
          }
          navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
          return;
        } else {
          postNotification("error", err);
        }
      }
    };

    go();
    // eslint-disable-next-line
  }, [activeTab]);

  // Filter and sort events
  useEffect(() => {
    const events =
      activeTab === 0
        ? playersCurrent
        : activeTab === 1
          ? playersPast
          : activeTab === 2
            ? playersFollowing
            : null;

    if (events?.length && filters.length) {
      const tempEvents = filterAndSortEvents(events, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, playersCurrent, playersPast, playersFollowing]);

  return (
    <EventsPlayDefault
      loading={loading}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      a11yProps={a11yProps}
      authContext={authContext}
      playersPast={playersPast}
      playersCurrent={playersCurrent}
      playersFollowing={playersFollowing}
      filters={filters}
      setFilters={setFilters}
      filteredEvents={filteredEvents}
      setFilteredEvents={setFilteredEvents}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      anchorEl2={anchorEl2}
      setAnchorEl2={setAnchorEl2}
      anchorEl3={anchorEl3}
      setAnchorEl3={setAnchorEl3}
      toggleMap={toggleMap}
      setToggleMap={setToggleMap}
      activeEvents={activeEvents}
      setActiveEvents={setActiveEvents}
      gameSystems={gameSystems}
      t={t}
    />
  );
}

export async function hasAccess(authContext, tabIndex, notify) {
  var err;

  if (authContext?.authState?.identity?.isAdmin === true) {
    return true;
  }

  if (tabIndex === 0) return true;
  // no need to check access for current tab
  else {
    if (NO_SUBSCRIPTIONS || authContext?.authState?.identity?.subscriber) {
      // subscriber or no subscribers turned on
      return true;
    } else {
      err = new Error(
        "Past events are hidden. You can unhide past events by becoming a Subscriber"
      );
      if (notify)
        postNotification(
          "warning",
          <>
            Past events are hidden. You can unhide past events by becoming a{" "}
            <Link href="/subscription" underline="none" target="_blank">
              Subscriber
            </Link>
          </>
        );
    }
  }

  if (err) {
    throw err;
  }
  return false;
}
